import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import type {
  InspectionAttachment,
  InspectionCheckpoint,
  StageCompletionInspection,
  StageInspectionMeasurement,
} from '@/services/qc-workflow.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export interface PrintableStageInspectionCompanyInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  taxNumber: string;
  logo?: string;
}

export interface PrintableStageInspectionProps {
  sci: StageCompletionInspection;
  workOrderNumber: string;
  checkpoints: InspectionCheckpoint[];
  measurements: StageInspectionMeasurement[];
  attachments: InspectionAttachment[];
  companyInfo: PrintableStageInspectionCompanyInfo;
  onClose?: () => void;
}

function dash(value?: string | number | null): string {
  if (value == null) return '—';
  const text = String(value).trim();
  return text ? text : '—';
}

function formatDate(value?: string | Date | null): string {
  if (!value) return '';
  return dayjs(value).format('DD MMM YYYY').toUpperCase();
}

function formatDimensionRequirement(m?: StageInspectionMeasurement): string {
  const d = m?.stageDimension;
  if (!d) return '—';
  if (d.specification_text && String(d.specification_text).trim())
    return String(d.specification_text).trim();
  if (d.tolerance_type === 'Min-Max' && d.min_value != null && d.max_value != null) {
    return `${d.min_value} - ${d.max_value}${d.unit ? ` ${d.unit}` : ''}`;
  }
  if (d.tolerance_type === 'Plus-Minus') {
    const plus = d.tolerance_plus != null ? d.tolerance_plus : '';
    const minus = d.tolerance_minus != null ? d.tolerance_minus : '';
    return `${d.nominal_value ?? ''}${d.unit ? d.unit : ''} +${plus} -${minus}`.trim();
  }
  return `${d.nominal_value ?? '—'}${d.unit ? ` ${d.unit}` : ''}`;
}

function formatMeasuredValue(m?: StageInspectionMeasurement): string {
  if (!m) return '—';
  if (m.actual_text != null && String(m.actual_text).trim() !== '')
    return String(m.actual_text).trim();
  if (m.measured_value != null && !Number.isNaN(Number(m.measured_value)))
    return String(m.measured_value);
  return '—';
}

/** Stage / dimensional inspection — A4 landscape via shared engine with unit columns. */
export const PrintableStageInspection: React.FC<PrintableStageInspectionProps> = ({
  sci,
  workOrderNumber,
  measurements,
  companyInfo,
  onClose,
}) => {
  const meta = (sci.report_metadata || {}) as Record<string, unknown>;

  const measuredByDimension = useMemo(() => {
    const grouped = new Map<
      string,
      {
        key: string;
        description: string;
        requirement: string;
        measurements: Map<number, StageInspectionMeasurement>;
        sequence: number;
      }
    >();

    measurements.forEach((measurement, index) => {
      const dimension = measurement.stageDimension;
      const key = measurement.stage_dimension_id || `row-${index}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          description: dimension?.dimension_name || `Dimension ${index + 1}`,
          requirement: formatDimensionRequirement(measurement),
          measurements: new Map<number, StageInspectionMeasurement>(),
          sequence: dimension?.sequence_number ?? index + 1,
        });
      }
      grouped.get(key)?.measurements.set(measurement.unit_sequence || 1, measurement);
    });

    return Array.from(grouped.values()).sort((a, b) => a.sequence - b.sequence);
  }, [measurements]);

  const unitColumns = useMemo(() => {
    const qty = Number(meta.qty);
    const measurementMax =
      measurements.reduce((max, item) => Math.max(max, Number(item.unit_sequence) || 0), 0) || 1;
    const count = Math.max(1, Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : measurementMax);
    return Array.from({ length: Math.min(count, 12) }, (_, index) => index + 1);
  }, [measurements, meta.qty]);

  const columns = [
    { key: 'sno', label: 'S. No.' },
    { key: 'description', label: 'Description' },
    { key: 'requirement', label: 'Required Dimension / Tol' },
    ...unitColumns.map((unit) => ({
      key: `u${unit}`,
      label: String(unit),
      align: 'center' as const,
    })),
  ];

  return (
    <EngineCommercialA4Print
      docType="stage_inspection"
      title="DIMENSIONAL INSPECTION REPORT"
      documentNumber={
        (meta.report_no as string | undefined) || sci.inspection_number || 'SCI'
      }
      partyName={(meta.client as string | undefined) || ''}
      onClose={onClose}
      landscape
      introText={companyInfo?.name || undefined}
      metaLines={[
        {
          label: 'JOB CARD No',
          value: dash((meta.job_card_no as string | undefined) || workOrderNumber),
        },
        {
          label: 'Drawing Reference',
          value: dash(meta.drawing_reference as string | undefined),
        },
        { label: 'Client', value: dash(meta.client as string | undefined) },
        { label: 'Revision', value: dash(meta.revision as string | undefined) },
        {
          label: 'Part No./Description',
          value: dash(meta.part_no_description as string | undefined),
        },
        { label: 'Heat Number', value: dash(meta.heat_number as string | undefined) },
        {
          label: 'Customer Job No',
          value: dash(meta.customer_job_no as string | undefined),
        },
        {
          label: 'Inspected By',
          value: dash(
            (meta.inspected_by as string | undefined) ||
              sci.inspector?.name ||
              sci.inspector?.email
          ),
        },
        { label: 'PO Line No', value: dash(meta.po_line_no as string | undefined) },
        { label: 'Material', value: dash(meta.material as string | undefined) },
        { label: 'Sign', value: dash(meta.sign as string | undefined) },
        { label: 'Qty', value: dash(meta.qty as number | undefined) },
        { label: 'Client PO No', value: dash(meta.client_po_no as string | undefined) },
        {
          label: 'Date of Inspection',
          value: formatDate(
            (meta.date_of_inspection as string | undefined) || sci.inspection_date
          ),
        },
        { label: 'Inspection No', value: sci.inspection_number || '' },
      ].filter((m) => m.value && m.value !== '—')}
      columns={columns}
      lines={
        measuredByDimension.length
          ? measuredByDimension.map((row, index) => {
              const cells: Record<string, string> = {
                requirement: dash(row.requirement),
              };
              for (const unit of unitColumns) {
                cells[`u${unit}`] = formatMeasuredValue(row.measurements.get(unit));
              }
              return {
                lineNumber: index + 1,
                description: dash(row.description),
                cells,
              };
            })
          : [
              {
                lineNumber: 1,
                description: 'No dimensional measurements saved.',
                cells: { requirement: '—' },
              },
            ]
      }
      finals={[
        { id: 'remarks', heightPx: 40 },
        { id: 'approval', heightPx: 40 },
        { id: 'form', heightPx: 28 },
      ]}
      finalBlocks={{
        remarks: (
          <p>
            <strong>Remarks:</strong>{' '}
            {dash((meta.remarks as string | undefined) || sci.observations || sci.findings)}
          </p>
        ),
        approval: (
          <div style={{ display: 'flex', gap: 32, fontSize: 11 }}>
            <div>
              <strong>Q.C. Approved By.</strong>{' '}
              {dash(meta.qc_approved_by as string | undefined)}
            </div>
            <div>
              <strong>Date of Approval:</strong>{' '}
              {formatDate(meta.qc_approval_date as string | undefined) || '—'}
            </div>
          </div>
        ),
        form: (
          <p style={{ fontSize: 10 }}>
            NTES-QSF-QA/QC-F02 Rev. 01 Effective Date: 09/10/2024
          </p>
        ),
      }}
    />
  );
};

export default PrintableStageInspection;
